<template> 
  <div class="place-search">
    <div class="place-search-inner"
      :class="{'expanded': state.isActive}"
      :style="`height:${state.isActive ? 2.5 + (state.results.length ? state.results.length*3.5+.5:0) + (message?2:0) : 2.5}rem`"
    >
      <div class="place-search-input">
        <AppIcon class="place-search-icon" name="search" />
        <input v-model="query"
          @focus="state.isActive = true"
          @blur="state.isActive = false"
          @keydown.up="up()"
          @keydown.down="down()"
          @keydown.enter="enter()"
          type="text"
          placeholder="Søg efter adresse..."
          spellcheck="false"
        />
        <div class="place-search-spinner">
          <AppLoader v-if="state.isLoading" :size="'small'"/>
        </div>
      </div>
      <div class="place-search-results">
        <div v-for="(result, i) in state.results"
            :key="i"
            class="search-result"
            :class="{'hover': state.current == i}"
            @mousedown="select(result)"
        >
          <div class="avatar" :class="result.type">
            <AppIcon :name="result.type == 'poi' ? 'place' :
                            result.type == 'address' ? 'home' :
                            result.type == 'place' ? 'location_city' :
                            'close'" />
          </div>
          <div style="overflow:hidden">
              <div class="title text-overflow-fade">{{ result.name }}</div>
              <div class="subtitle text-overflow-fade">{{ result.address.split(',').slice(1).join(',').replace(', Denmark','') }}</div>
          </div>
          <div class="add-place">
            <AppIcon :name="state.current == i ? 'keyboard_return' : 'add'" />
          </div>
        </div>
        <div class="place-search-message">{{ message }}</div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, reactive } from 'vue'
import { debounceRef } from '@/api/tools'
import { searchLocations } from '@/services/map.service'
import { Location } from '@/api/models/Location'

export default defineComponent({
  name: 'PlaceSearch',
  emits: ['selected'],
  setup (props, { emit }) {
    
    const query = ref('')
    const state = reactive<{
      results: Location[];
      isLoading: boolean;
      isActive: boolean;
      current: number;
    }>({
      results: [],
      isLoading: false,
      isActive: false,
      current: 0
    })
    
    const message = computed(() => !state.results.length && query.value && !state.isLoading ? 'Ingen resultater' : '')

    debounceRef(query, async (q) => {      
      if (q) {      
        state.current = 0
        state.isLoading = true
        state.results = await searchLocations(q, 5)
        state.isLoading = false
      }
    }, 500)

    function select(location: Location) {
      emit('selected', location)
      query.value = ''
      state.results = []
    }
    function up() {
      state.current > 0 && state.current--
    }
    function down() {
      state.current < state.results.length - 1 && state.current++
    }
    function enter() {
      const location = state.results[state.current]
      location && select(location)
    }

    return { state, query, up, down, enter, select, message }
  }
})
</script>

<style lang="scss" scoped>
.place-search {
  width: 100%;
  height: 2.5rem;
  position: relative;

  &-message {
    padding: 0 1rem;
  }

  &-inner {
    background: var(--color-card);
    border-radius: 1rem;
    position: absolute;
    z-index: 100000;
    top: 0;
    right: 0;
    left: 0;
    height: 2.5rem;
    transition: all .2s;
    overflow: hidden;

    &.expanded {
      background: var(--color-card-elevated);
      box-shadow: var(--card-shadow);

      .place-search-input {
        background: var(--color-card-elevated);
      }
    }
  }
}
.place-search-input {
  border-radius: 1rem;
  height: 2.5rem;
  background: var(--color-background-input);
  transition: background .2s;
  
  input {
    width: 100%;
    height: 100%;
    background: 0;
    border: 0;
    padding: 0 1rem;
    padding-left: 2.5rem;
    border-radius: 1rem;
    outline: 0 !important;
  }
}

.place-search-results {
  .search-result {
    transition: background .2s;
    cursor: pointer;
    padding: .5rem 1rem;
    display: grid;
    grid-template-columns: 1.5rem auto 1.5rem;
    grid-gap: 1rem;
    height: 3.5rem;
    overflow: hidden;

    &:hover, &.hover {
      background: var(--color-background-input);
      
      .add-place {
        transform: scale(1.1);
        background: var(--color-primary);
        color: white;
      }
    }
    .title {
      font-size: .875rem;
      font-weight: 600;
      line-height: 1.5rem;
    }
    .subtitle {
      font-size: .8125rem;
      font-weight: 400;
      line-height: 1rem;
      color: var(--text-color-secondary);
    }
    .avatar {
      color: white;
      height: 1.5rem;
      width: 1.5rem;
      border-radius: .5rem;
      display: flex;
      justify-content: center;
      align-items: center;
      margin-top: .25rem;

      &.poi {
        color: rgb(29, 189, 201);
      }
      &.place {
        color: rgb(29, 201, 149);
      }
      &.address {
        color: #06f;
      }
    }

    .add-place {
      height: 1.5rem;
      width: 1.5rem;
      margin-top: .5rem;
      border-radius: 1rem;
      transition: all .2s;
      display: flex;
      justify-content: center;
      align-items: center;
      font-weight: 500;
      background: var(--text-color-tertiary);
      color: var(--text-color-secondary);
      .icon {
        font-size: 1rem;
      }
    }

  }
}

.place-search-icon {
  position: absolute;
  display: flex;
  top: .5rem;
  left: .5rem;
  color: var(--text-color-secondary);
  pointer-events: none;
}
.place-search-spinner {
  position: absolute;
  display: flex;
  top: .5rem;
  right: .5rem;
}
</style>